import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '@src/api_service/useApi';

interface AuthContextProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  userProfile: AdminDetailsProps | null;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

// Custom hook to use AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userProfile, setUserProfile] = useState<AdminDetailsProps | null>(null);

  const { loading, get } = useApi();

  const refreshUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setIsAuthenticated(false);
        setUserProfile(null);
        return;
      }

      await get<{ data: AdminDetailsProps }, any>(
        `/details`,
        { Authorization: `Bearer ${token}` },
        {
          onSuccess: (response) => {
            if (response.data) {
              setUserProfile(response.data);
              setIsAuthenticated(true);
            } else {
              // Handle case where data is missing
              localStorage.removeItem('token'); // Clear invalid token
              setIsAuthenticated(false);
              setUserProfile(null);
              toast.error('Failed to authenticate');
            }
          },
          onError: (err: any) => {
            console.error('Error fetching user profile:', err);
            // localStorage.removeItem('token'); // Clear invalid token
            setIsAuthenticated(false);
            setUserProfile(null);
            toast.error(err.message || 'Failed to authenticate');
          }
        }
      );
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  };

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const pathname = window.location.pathname;
      
      if (token && pathname.includes('/home/')) {
        refreshUserProfile();
      } else {
        setIsLoading(false);
        setIsAuthenticated(!!token);
      }
    };

    checkAuth();

    // Listen for pathname changes
    const handleRouteChange = () => {
      const pathname = window.location.pathname;
      const token = localStorage.getItem('token');
      
      if (token && pathname.includes('/home/') && !userProfile) {
        refreshUserProfile();
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        isLoading, 
        userProfile, 
        refreshUserProfile 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
